import { useSelector, useDispatch } from "react-redux";
import { Field, touch } from "redux-form";
import { Form, Spinner } from "react-bootstrap";
import { useTheme } from "styled-components";
import {
  GBLI_EMAIL_EVENTS,
  GBLI_PHONE_EVENTS_TITLE,
} from "@gbli-events/common/src/Constants/shared";
import { accepted, required } from "@gbli-events/common/src/Helpers/validators";
import {
  ENTITY_COMPANY,
  ENTITY_PERSON,
} from "@gbli-events/common/src/Constants/entityTypes";
import ReactRouterPropTypes from "react-router-prop-types";
import { formName } from "Constants/constants";
import { createStateOptions } from "../../Helpers/StateSelectOptions";
import { getUsStates } from "Selectors/Shared";
import {
  getIsPaymentPageValid,
  insuredPaymentHasErrors,
} from "Selectors/Payment";
import getPaymentBody from "Helpers/ExtractFormState";
import Alert from "@gbli-events/common/src/Components/Themed/Alert";
import { getMainVenue } from "Selectors/Venue";
import { getUserEntryDataPayload } from "Selectors/UserEntryData";
import { getInsuranceCompanyByState } from "Selectors/InsuranceCompany";
import { getDashedInsuredName } from "Selectors/InsuredInformation";
import { useInsuranceCompanies } from "Hooks/InsuranceCompanies";
import { confirmInsuredAndPaymentForm } from "Actions/payment";
import FormStepNavigation from "Components/FormStepNavigation";
import CompanyFieldGroup from "@gbli-events/common/src/Components/FormElements/CompanyFieldGroup";
import AddressFieldGroup from "@gbli-events/common/src/Components/FormElements/AddressFieldGroup";
import RadioFieldGroup from "@gbli-events/common/src/Components/FormElements/RadioFieldGroup/RadioFieldGroup";
import AsyncButton from "@gbli-events/common/src/Components/AsyncButton";
import TextField from "@gbli-events/common/src/Components/FormElements/TextField";
import StyledCheckboxField from "@gbli-events/common/src/Components/FormElements/CheckboxField/StyledCheckboxField";
import EmailPhoneFieldGroup from "@gbli-events/common/src/Components/FormElements/EmailPhoneFieldGroup";
import { getOrCreatePaymentClientSessionId } from "../../Helpers/PaymentClientSession";
import { useRxCoverageApplication } from "Features/CoverageApplication/Components/RxCoverageApplicationProvider";
import ThemeColor from "@gbli-events/common/src/Components/Themed/ThemeColor";

const propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
};

const InsuredPayment = () => {
  useInsuranceCompanies();

  const usStatesOptions = createStateOptions(useSelector(getUsStates));
  const basePayload = useSelector(getPaymentBody);
  const theme = useTheme();
  const mainVenue = useSelector(getMainVenue);
  const insuranceCompany = useSelector(
    getInsuranceCompanyByState(mainVenue.selectedPlaceAddressComponents.state),
  );
  const userEntryData = useSelector(getUserEntryDataPayload);
  const dashedInsuredName = useSelector(getDashedInsuredName);
  const formHasErrors = useSelector(insuredPaymentHasErrors);
  const venueState = basePayload.venueAddress[0]?.state;
  const dispatch = useDispatch();

  const isPaymentPageValid = useSelector(getIsPaymentPageValid);

  const { contactSame, insuredRenter } = useSelector(
    (state) => state.form.application.values,
  );
  const isContactSame = contactSame === "1";
  const [
    {
      payment: {
        initSessionRequest: sessionState,
        modalRenderError: oneIncModalError,
      },
    },
    {
      payment: { initSessionRequest: sessionActions },
    },
  ] = useRxCoverageApplication();

  const apiError = Boolean(sessionState.error);

  const isSessionLoading = sessionState?.loading ?? false;

  const handlePayNow = () => {
    dispatch(confirmInsuredAndPaymentForm());

    if (isPaymentPageValid) {
      sessionActions.send({
        ...basePayload,
        clientSessionId: getOrCreatePaymentClientSessionId(),
        userEntryData,
        dashedInsuredName,
      });
    }
  };

  return (
    <>
      <div className="insured-information container">
        <FormStepNavigation />
        <div className="page-heading">
          <h2>Insured Information</h2>
        </div>
        <RadioFieldGroup
          controlName="insuredRenter"
          label="Who is renting the venue?"
          options={[
            {
              label: "Individual",
              value: ENTITY_PERSON,
              onChange: () => {
                if (formHasErrors) {
                  dispatch(
                    touch(formName, "insuredFirstName", "insuredLastName"),
                  );
                }
              },
            },
            {
              label: "Company/Organization",
              value: ENTITY_COMPANY,
              onChange: () => {
                if (formHasErrors) {
                  dispatch(touch(formName, "insuredCompany"));
                }
              },
            },
          ]}
          validators={[required]}
        />
        <Form.Group>
          {insuredRenter === ENTITY_PERSON ? (
            <>
              <div className="form-row">
                <div className="col-sm">
                  <Field
                    component={TextField}
                    label="First Name"
                    placeholder="First Name"
                    ariaLabel="First Name"
                    validate={[required]}
                    required
                    name="insuredFirstName"
                  />
                </div>
                <div className="col-sm">
                  <Field
                    component={TextField}
                    label="Last Name"
                    placeholder="Last Name"
                    ariaLabel="Last Name"
                    validate={[required]}
                    required
                    name="insuredLastName"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <CompanyFieldGroup
                companyNameControl="insuredCompany"
                companyTypeControl="insuredCompanyType"
                theme={theme}
              />
            </>
          )}
        </Form.Group>
        <AddressFieldGroup
          theme={theme}
          usStatesOptions={usStatesOptions}
          streetAddressControl="insuredAddress"
          addressLine2Control="insuredAddress2"
          cityControl="insuredCity"
          stateControl="insuredState"
          zipControl="insuredZip"
        />
      </div>

      <div className="insured-information container">
        <div className="page-heading">
          <h2>Insured Contact</h2>
        </div>
        <p className="mb-3">
          <span className="label">
            Enter a contact for the insurance policy
          </span>
          <br />
          <i>
            We will use this information to email a copy of your proof of
            insurance, or to contact you about the policy.{" "}
          </i>
        </p>
        {/* If the insured is of type PERSON */}
        {insuredRenter === ENTITY_PERSON && (
          <>
            <RadioFieldGroup
              controlName="contactSame"
              label="Is the insured contact the same as the insured?"
              options={[
                { label: "Yes", value: "1" },
                {
                  label: "No",
                  value: "0",
                  onChange: () => {
                    if (formHasErrors) {
                      dispatch(
                        touch(formName, "contactFirstName", "contactLastName"),
                      );
                    }
                  },
                },
              ]}
              validators={[required]}
            />
            <div className="form-group">
              <div className="form-row">
                <>
                  <div className="col-sm">
                    <Field
                      component={TextField}
                      label="First Name"
                      validate={[required]}
                      required
                      name={
                        isContactSame ? "insuredFirstName" : "contactFirstName"
                      }
                      type="text"
                      readOnly={isContactSame}
                    />
                  </div>
                  <div className="col-sm">
                    <Field
                      component={TextField}
                      label="Last Name"
                      validate={[required]}
                      required
                      name={
                        isContactSame ? "insuredLastName" : "contactLastName"
                      }
                      type="text"
                      readOnly={isContactSame}
                    />
                  </div>
                </>
              </div>
            </div>
            <EmailPhoneFieldGroup
              phoneControl="contactPhone"
              emailControl="contactEmail"
            />
          </>
        )}

        {/* If the insured is of type COMPANY */}
        {insuredRenter === ENTITY_COMPANY && (
          <>
            <div className="form-group">
              <div className="form-row">
                <div className="col-sm">
                  <Field
                    component={TextField}
                    label="First Name"
                    validate={[required]}
                    name="contactFirstName"
                    type="text"
                    required
                  />
                </div>
                <div className="col-sm">
                  <Field
                    component={TextField}
                    label="Last Name"
                    validate={[required]}
                    name="contactLastName"
                    type="text"
                    required
                  />
                </div>
              </div>
            </div>
            <EmailPhoneFieldGroup
              phoneControl="contactPhone"
              emailControl="contactEmail"
            />
          </>
        )}
      </div>

      <div className="payment">
        <div className="container">
          {(apiError || oneIncModalError) && (
            <Alert variant="error" className="mt-3">
              <i className="far fa-exclamation-circle alert__icon" />
              <div className="alert__text">
                {apiError
                  ? "Sorry, we were unable to open the payment form. Please try again or contact us at "
                  : "Sorry, there was an issue processing your payment. Please try again or contact us at "}
                <span className="text-nowrap">{GBLI_PHONE_EVENTS_TITLE}</span>{" "}
                or {GBLI_EMAIL_EVENTS} for further assistance.
              </div>
            </Alert>
          )}

          {insuranceCompany && (
            <Field
              name="consentConfirmed"
              validate={accepted}
              component={StyledCheckboxField}
              label={`I declare under penalty of perjury that the foregoing information provided in the application is true and correct, and that any misstatement of fact in the information given, which if known to J.H. Ferguson and Associates would have caused ${insuranceCompany.company} to decline this application, is grounds for voiding the policy. I verify that if I discover or become aware of any significant change in the conditions stated in this Application between the date of this Application and the policy inception date, which would render the Application inaccurate or incomplete, notice of such change will be reported in writing to J.H Ferguson and Associates immediately and any outstanding policy may be modified or withdrawn.`}
            />
          )}
          {venueState === "ND" && (
            <Field
              name="northDakotaDisclosure"
              validate={accepted}
              component={StyledCheckboxField}
              label={
                <>
                  <p>
                    <b>North Dakota Disclosure</b>
                  </p>
                  <p>Notice:</p>
                  <ol>
                    <li>
                      An insurer that is not licensed in this state is issuing
                      the insurance policy that you have applied to purchase.
                      These companies are called "nonadmitted" or "surplus
                      lines" insurers.
                    </li>
                    <li>
                      The insurer is not subject to the financial solvency
                      regulation and enforcement that applies to licensed
                      insurers in this state.
                    </li>
                    <li>
                      These insurers generally do not participate in insurance
                      guaranty funds created by state law. These guaranty funds
                      will not pay your claims or protect your assets if the
                      insurer becomes insolvent and is unable to make payments
                      as promised.
                    </li>
                    <li>
                      Some states maintain lists of approved or eligible surplus
                      lines insurers and surplus lines producers may use only
                      insurers on the lists. Some states issue orders that
                      particular surplus lines insurers cannot be used.
                    </li>
                    <li>
                      For additional information about the above matters and
                      about the insurer, you should ask questions of your
                      insurance producer or surplus lines producer. You may also
                      contact your insurance department consumer help line.
                    </li>
                  </ol>
                </>
              }
            />
          )}

          {formHasErrors && (
            <Alert variant="danger">
              <i className="far fa-exclamation-circle alert__icon" />
              <div className="alert__text">
                Please correct the errors above.
              </div>
            </Alert>
          )}

          <AsyncButton
            className="continue-btn"
            spinning={isSessionLoading}
            spinningText="Processing"
            disabled={formHasErrors || isSessionLoading}
            onClick={handlePayNow}
          >
            PAY NOW
          </AsyncButton>
        </div>
      </div>
    </>
  );
};

InsuredPayment.propTypes = propTypes;

export default InsuredPayment;
